import { useState, useMemo } from "react";

const allCities = [
  {
    name: "Nashville",
    route: "/nashville",
    tagline: "Davidson County — Tennessee's capital and primary travel hub for Middle Tennessee. Music Row, Vanderbilt Medical, and the center of Tennessee's international travel demand.",
    highlights: ["Atlanta Passport Agency — closest regional agency", "Serving Franklin, Brentwood, Murfreesboro"],
    badge: "Most Popular",
    status: "live",
  },
  {
    name: "Memphis",
    route: "/memphis",
    tagline: "Shelby County expedited passport services for West Tennessee travelers near the Mississippi River",
    highlights: ["FedEx & St. Jude Medical coverage", "Serving Germantown, Collierville, Bartlett"],
    badge: "Popular",
    status: "live",
  },
  {
    name: "Knoxville",
    route: "/knoxville",
    tagline: "Knox County expedited passport processing near the University of Tennessee campus and Smoky Mountains gateway",
    highlights: ["University & research hub coverage", "Serving Maryville, Oak Ridge, Sevierville"],
    badge: null,
    status: "live",
  },
  {
    name: "Chattanooga",
    route: "/chattanooga",
    tagline: "Hamilton County expedited passport services near Lookout Mountain and the Tennessee River Gorge",
    highlights: ["Manufacturing & outdoor hub coverage", "Serving Cleveland, Hixson, East Brainerd"],
    badge: null,
    status: "live",
  },
  {
    name: "Clarksville",
    route: "/clarksville",
    tagline: "Montgomery County expedited passport services for Fort Campbell military and North Tennessee travelers",
    highlights: ["Fort Campbell military coverage", "Serving Hopkinsville, Oak Grove"],
    badge: "Military Specialist",
    status: "live",
  },
  {
    name: "Murfreesboro",
    route: "/murfreesboro",
    tagline: "Rutherford County expedited passport processing in Middle Tennessee's fastest-growing city",
    highlights: ["MTSU study abroad support", "Serving Smyrna, La Vergne, Nashville metro"],
    badge: null,
    status: "live",
  },
  {
    name: "Franklin",
    route: "/franklin-tn",
    tagline: "Williamson County expedited passport services in Tennessee's wealthiest suburb — serving Cool Springs executives, Brentwood families, and Spring Hill residents.",
    highlights: ["Cool Springs corporate coverage", "Serving Brentwood, Spring Hill, Thompson's Station"],
    badge: "Affluent Suburb",
    status: "live",
  },
  {
    name: "Johnson City",
    route: "/johnson-city",
    tagline: "Washington County expedited passport services in the Tri-Cities region. ETSU study abroad specialists and Mountain States medical professional coverage.",
    highlights: ["ETSU study abroad support", "Serving Kingsport, Bristol, Jonesborough"],
    badge: "Tri-Cities Hub",
    status: "live",
  },
  {
    name: "Kingsport",
    route: "/kingsport",
    tagline: "Sullivan County expedited passport processing for Eastman Chemical executives, BAE Systems contractors, and Tri-Cities professionals.",
    highlights: ["Eastman Chemical coverage", "Serving Johnson City, Bristol, Gate City VA"],
    badge: "Corporate Specialist",
    status: "live",
  },
  {
    name: "Jackson",
    route: "/jackson",
    tagline: "Madison County expedited passport services in West Tennessee between Memphis and Nashville",
    highlights: ["West Tennessee coverage", "Serving Humboldt, Milan, Lexington"],
    badge: null,
    status: "live",
  },
  {
    name: "Cookeville",
    route: "/cookeville",
    tagline: "Putnam County expedited passport processing in the Cumberland Plateau near Tennessee Tech University",
    highlights: ["Upper Cumberland coverage", "Serving Sparta, Crossville, Livingston"],
    badge: null,
    status: "live",
  },
  {
    name: "Bristol",
    route: "/bristol",
    tagline: "Sullivan County expedited passport services in the Tennessee-Virginia border city birthplace of country music",
    highlights: ["TN-VA border coverage", "Serving Kingsport, Johnson City, Abingdon VA"],
    badge: null,
    status: "live",
  },
  {
    name: "Oak Ridge",
    route: "/oak-ridge",
    tagline: "Anderson County expedited passport services near Oak Ridge National Laboratory — East Tennessee",
    highlights: ["National lab coverage", "Serving Clinton, Norris, Lake City"],
    badge: null,
    status: "live",
  },
  {
    name: "Maryville",
    route: "/maryville-tn",
    tagline: "Blount County expedited passport processing near McGhee Tyson Airport and the Smoky Mountains gateway",
    highlights: ["Blount County coverage", "Serving Alcoa, Friendsville, Louisville"],
    badge: null,
    status: "live",
  },
  {
    name: "Columbia",
    route: "/columbia-tn",
    tagline: "Maury County expedited passport services in South Middle Tennessee between Nashville and the Tennessee Valley",
    highlights: ["South Middle Tennessee coverage", "Serving Spring Hill, Mount Pleasant, Lawrenceburg"],
    badge: null,
    status: "live",
  },
];

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export default function TennesseeCitiesDirectory() {
  const [activeLetter, setActiveLetter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const lettersWithCities = useMemo(() => {
    const set = new Set<string>();
    allCities.forEach((c) => set.add(c.name[0].toUpperCase()));
    return set;
  }, []);

  const filteredCities = useMemo(() => {
    let result = allCities;
    if (activeLetter) {
      result = result.filter((c) => c.name[0].toUpperCase() === activeLetter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.tagline.toLowerCase().includes(q) ||
          c.highlights.some((h) => h.toLowerCase().includes(q))
      );
    }
    return result;
  }, [activeLetter, searchQuery]);

  const liveCount = allCities.filter((c) => c.status === "live").length;
  const comingCount = allCities.filter((c) => c.status === "coming").length;

  return (
    <section id="cities" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">
            Tennessee Cities
          </p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Choose Your Tennessee Location
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed mb-2">
            Select your city to view local acceptance facilities, processing options, and
            expedited services tailored to your area.
          </p>
          <p className="text-gray-400 text-sm">
            <span className="text-[#1A365D] font-semibold">{liveCount}</span> cities live
            &nbsp;&middot;&nbsp;
            <span className="text-[#1A365D] font-semibold">{comingCount}</span> more coming soon
          </p>
        </div>

        {/* Search bar */}
        <div className="max-w-xl mx-auto mb-6">
          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
              <i className="ri-search-line text-gray-400"></i>
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                if (e.target.value.trim() && activeLetter) setActiveLetter(null);
              }}
              placeholder="Search cities..."
              className="w-full pl-11 pr-10 py-3 bg-white border border-gray-200 rounded-lg text-sm text-[#1A365D] placeholder:text-gray-400 focus:outline-none focus:border-[#F6C344] focus:ring-1 focus:ring-[#F6C344] transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line"></i>
              </button>
            )}
          </div>
        </div>

        {/* Alphabet bar */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="flex flex-wrap items-center justify-center gap-1.5 md:gap-2">
            <button
              onClick={() => setActiveLetter(null)}
              className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-all whitespace-nowrap ${
                activeLetter === null
                  ? "bg-[#1A365D] text-white"
                  : "bg-white text-gray-500 hover:bg-[#EEF2F8] hover:text-[#1A365D] border border-gray-200"
              }`}
            >
              All
            </button>
            {LETTERS.map((letter) => {
              const hasCities = lettersWithCities.has(letter);
              const isActive = activeLetter === letter;
              return (
                <button
                  key={letter}
                  onClick={() => hasCities && setActiveLetter(isActive ? null : letter)}
                  disabled={!hasCities}
                  className={`w-8 h-8 md:w-9 md:h-9 rounded-md text-sm font-semibold flex items-center justify-center transition-all ${
                    isActive
                      ? "bg-[#1A365D] text-white"
                      : hasCities
                        ? "bg-white text-[#1A365D] hover:bg-[#EEF2F8] border border-gray-200 cursor-pointer"
                        : "bg-gray-100 text-gray-300 border border-gray-100 cursor-not-allowed"
                  }`}
                >
                  {letter}
                </button>
              );
            })}
          </div>
        </div>

        {/* Results count */}
        {(activeLetter || searchQuery) && (
          <div className="flex items-center justify-between max-w-7xl mb-6">
            <p className="text-sm text-gray-500">
              Showing <span className="font-semibold text-[#1A365D]">{filteredCities.length}</span>{" "}
              {filteredCities.length === 1 ? "city" : "cities"}
              {activeLetter && (
                <> starting with <span className="font-semibold text-[#1A365D]">{activeLetter}</span></>
              )}
              {searchQuery && (
                <> matching <span className="font-semibold text-[#1A365D]">&ldquo;{searchQuery}&rdquo;</span></>
              )}
            </p>
            <button
              onClick={() => { setActiveLetter(null); setSearchQuery(""); }}
              className="text-sm text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors whitespace-nowrap"
            >
              Clear filters
            </button>
          </div>
        )}

        {/* City cards grid */}
        {filteredCities.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCities.map((city, i) =>
              city.status === "live" ? (
                <a
                  key={i}
                  href={city.route}
                  className="group relative bg-white border border-gray-200 rounded-lg p-7 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
                >
                  {city.badge && (
                    <span className="absolute top-4 right-4 bg-[#1A365D] text-white text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap">
                      {city.badge}
                    </span>
                  )}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0">
                      <i className="ri-map-pin-2-line text-[#1A365D] text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-[#1A365D] text-lg">{city.name}</h3>
                      <p className="text-gray-400 text-xs">Tennessee</p>
                    </div>
                  </div>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4">{city.tagline}</p>
                  <ul className="space-y-2 mb-6 flex-1">
                    {city.highlights.map((h, j) => (
                      <li key={j} className="flex items-center gap-2 text-sm text-gray-600">
                        <i className="ri-check-line text-[#F6C344] flex-shrink-0"></i>
                        {h}
                      </li>
                    ))}
                  </ul>
                  <div className="inline-flex items-center gap-1.5 text-[#1A365D] font-semibold text-sm group-hover:text-[#F6C344] transition-colors mt-auto whitespace-nowrap">
                    View Services <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform"></i>
                  </div>
                </a>
              ) : (
                <div
                  key={i}
                  className="group relative bg-white border border-dashed border-gray-300 rounded-lg p-7 flex flex-col"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-100 flex-shrink-0">
                      <i className="ri-time-line text-gray-400 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-400 text-lg">{city.name}</h3>
                      <span className="inline-block bg-gray-100 text-gray-500 text-xs font-semibold px-2 py-0.5 rounded-full mt-0.5">Coming Soon</span>
                    </div>
                  </div>
                  <p className="text-gray-400 text-sm leading-relaxed mb-4">{city.tagline}</p>
                  <ul className="space-y-2 mb-6 flex-1">
                    {city.highlights.map((h, j) => (
                      <li key={j} className="flex items-center gap-2 text-sm text-gray-400">
                        <i className="ri-check-line text-gray-300 flex-shrink-0"></i>
                        {h}
                      </li>
                    ))}
                  </ul>
                  <div className="inline-flex items-center gap-1.5 text-gray-400 font-semibold text-sm mt-auto whitespace-nowrap">
                    Coming Soon <i className="ri-time-line"></i>
                  </div>
                </div>
              )
            )}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#EEF2F8] mx-auto mb-4">
              <i className="ri-search-line text-[#1A365D] text-2xl"></i>
            </div>
            <h3 className="font-bold text-[#1A365D] text-lg mb-2">No cities found</h3>
            <p className="text-gray-500 text-sm max-w-md mx-auto">Try a different search term or letter. We are always adding new Tennessee locations.</p>
            <button
              onClick={() => { setActiveLetter(null); setSearchQuery(""); }}
              className="mt-4 inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-sm transition-colors whitespace-nowrap"
            >
              Show all cities <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}